<template>
  <div class="search-result-products">
    <b-row :cols="contentCols.xs" :cols-sm="contentCols.sm" :cols-md="contentCols.md" :cols-lg="contentCols.lg" :cols-xl="contentCols.xl">
      <b-col v-for="result in results" :key="result.id">
        <c-teaser :noImg="result.noImg" :tagText="result.event.type" :tagVariant="result.event.typeVariant" :languageText="result.event.language ? $T(`Language${result.event.language}`) : null"
                  :stateText="result.event.state" :stateVariant="result.event.stateVariant" :href="result.href" :imgType="result.imgType" :imgSrc="result.imgSrc" :imgAlt="result.imgAlt"
                  :hideArrow="teaserHideArrow(result)">
          <h3><a class="teaser-link stretched-link" :href="result.href">{{ result.title }}</a></h3>
          <p v-if="result.product.portioning">{{ result.product.portioning }}</p>
          <p v-if="result.text">{{ result.text }}</p>
          <slot name="resultadditionalinfo" v-bind="{ resultItem: result }" />
          <p v-if="result.event.date && result.event.location">
            <strong>
              <timeago :datetime="result.event.date" :converterOptions="{ format: 'P', forceDateFormat: true }" />
            </strong>, {{ result.event.location }}
          </p>

          <template v-if="result.alternativeEvents.length > 0">
            <p v-if="!result.showAlternativeEvents">
              <b-link @click.stop="result.showAlternativeEvents = true">{{ $T('AlternativeEventsButton') }}</b-link>
            </p>

            <template v-else>
              <p v-for="event in result.alternativeEvents" :key="event.id">
                <a :href="event.url"><strong>
                    <timeago :datetime="event.date" :converterOptions="{ format: 'P', forceDateFormat: true }" />
                  </strong>, {{ event.location }}</a>
              </p>
            </template>
          </template>
          <template v-if="showPackshotBasketButton">
            <c-packshot-basket-button :productId="result.event.id" :basketButtonText="$T('PackshotBasketButtonText')" :basketButtonRemoveText="$T('PackshotBasketRemoveButtonText')" />
          </template>
          <template #overlay-controls v-if="isLoggedIn && isBuyableResult(result) && !isMarketingProduct(result)">
            <c-wish-list-basket-button :productId="result.id" :basketButtonText="$T('WishListBasketButtonText')" :basketButtonRemoveText="$T('WishListBasketRemoveButtonText')" />
          </template>
          <template #footer-controls v-if="isLoggedIn && isMarketingDirectCart(result)">
            <div class="search-marketing-purchase">
              <c-product-basketbutton
                class="purchase-basket-select"
                :product-id="result.id"
                :small="true"
                :quantityinfo="marketingQuantityInfo"
                :quantity-post-fix-singular="marketingBundlePostFixSingular(result)"
                :quantity-post-fix-plural="marketingBundlePostFixPlural(result)"
                :translations="TRANSLATIONS.productbuybutton"
                :use-i18n="true"
                :enable-marketing-product-upsell="false"
                :error-toast-title="marketingBasketErrorTitle"
              >
                <b-icon icon="cart" :title="$T('addToBasket')" />
              </c-product-basketbutton>
            </div>
          </template>
          <template #footer-controls v-else-if="isLoggedIn && isBuyableResult(result)">
            <div class="search-product-purchase justify-content-between flex-sm-column flex-xl-row justify-content-sm-center justify-content-xl-between align-items-center">
              <div class="d-flex align-items-left align-self-sm-start align-self-xl-center">
                <div class="purchase-availability">
                  <b-icon v-if="isOutOfStock(result)" icon="b-x-circle-fill" class="availability-icon not-available" :title="$T('notavailable')" />
                  <b-icon v-else icon="b-check-circle-fill" class="availability-icon available" :title="$T('available')" />
                </div>
                <div class="purchase-price">
                  <span class="price-value">{{ formatPrice(result.purchase.priceinfo.price) }}</span>
                </div>
              </div>
              <div class="purchase-cart align-self-sm-end align-self-xl-center ml-sm-0">
                <c-product-basketbutton
                                        v-if="isAvailable(result)"
                                        class="purchase-basket-select"
                                        :productId="result.id"
                                        :small="true"
                                        :translations="TRANSLATIONS.productbuybutton"
                                        :useI18n="true">
                  <b-icon icon="cart" :title="$T('addToBasket')" />
                </c-product-basketbutton>
                <b-button
                  v-else-if="isOutOfStock(result)"
                  class="purchase-action-button"
                  :class="isRestockSubscribedFor(result) ? 'is-primary' : 'is-muted'"
                  :title="$T('notifyMe')"
                  @click.stop.prevent="onNotifyClick(result)"
                >
                  <b-icon icon="b-bell-fill" />
                </b-button>
                <b-button v-else-if="isNotOrderableEligibility(result)" class="purchase-action-button is-muted" :title="$T('notOrderableEligibility')" disabled @click.stop.prevent>
                  <b-icon icon="cart" />
                </b-button>
                <b-button v-else class="purchase-action-button is-muted" :title="getStatusText(result)" disabled @click.stop.prevent>
                  <b-icon icon="cart" />
                </b-button>
              </div>
            </div>
          </template>
        </c-teaser>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import { ASIDE_COLUMN_WIDTHS } from '@/assets/js/config/client';
import { PRODUCT_ORDER_STATUS } from '@/constants';
import { getProductRestockSubscriptionStatus, toggleProductRestockSubscription } from '@/assets/js/helper/product-restock-subscription';

export default {
  name: 'Search.ResultstypeProduct',
  props: {
    resultType: {
      type: String
    },
    results: {
      type: Array,
      default: () => ([])
    },
    showPackshotBasketButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      contentCols: ASIDE_COLUMN_WIDTHS,
      PRODUCT_ORDER_STATUS,
      restockSubscribedByProductId: {},
      marketingQuantityInfo: {
        min: 1,
        max: 1000,
        step: 1,
        initialStep: 1
      }
    }
  },
  computed: {
    marketingBasketErrorTitle() {
      const t = this.TRANSLATIONS?.productbuybutton?.marketingproductdetail?.errortitle
      return typeof t === 'string' ? t : ''
    },
    isLoggedIn() {
      return !!this.$store.getters['profile/getUserId']
    }
  },
  watch: {
    results: {
      deep: true,
      handler: 'refreshRestockSubscriptionStatuses'
    },
    isLoggedIn: 'refreshRestockSubscriptionStatuses'
  },
  mounted() {
    this.refreshRestockSubscriptionStatuses()
  },
  methods: {
    isRestockSubscribedFor(result) {
      if (!result?.id) return false
      return !!this.restockSubscribedByProductId[result.id]
    },
    async refreshRestockSubscriptionStatuses() {
      if (!this.isLoggedIn || !this.results?.length) {
        this.restockSubscribedByProductId = {}
        return
      }
      const ids = [
        ...new Set(this.results.filter(r => this.isOutOfStock(r) && r?.id).map(r => r.id))
      ]
      if (!ids.length) {
        this.restockSubscribedByProductId = {}
        return
      }
      const entries = await Promise.all(
        ids.map(async (id) => {
          const { subscribed } = await getProductRestockSubscriptionStatus(id)
          return [id, subscribed]
        })
      )
      const next = {}
      entries.forEach(([id, subscribed]) => {
        if (subscribed) next[id] = true
      })
      this.restockSubscribedByProductId = next
    },
    isBuyableResult(result) {
      return !!(result?.id && result?.purchase?.hasEligibilityData)
    },
    isMarketingProduct(result) {
      return result?.searchType === 'marketingproduct'
    },
    isMarketingDirectCart(result) {
      return this.isMarketingProduct(result) && result?.marketing && !result.marketing.isStoreWindow
    },
    teaserHideArrow(result) {
      if (this.isMarketingProduct(result)) {
        return this.isLoggedIn && this.isMarketingDirectCart(result)
      }
      return this.resultType === 'product' && this.isLoggedIn
    },
    marketingBundlePostFixSingular(result) {
      const bc = (result?.marketing?.bundleCount || '').toString().trim()
      if (!bc) return ''
      return this.$T('marketing.bundlebasketamountsingular', { 0: bc })
    },
    marketingBundlePostFixPlural(result) {
      const bc = (result?.marketing?.bundleCount || '').toString().trim()
      if (!bc) return ''
      return this.$T('marketing.bundlebasketamountplural', { 0: bc })
    },
    isAvailable(result) {
      return result?.purchase?.orderStatus === PRODUCT_ORDER_STATUS.AVAILABLE
    },
    isOutOfStock(result) {
      return result?.purchase?.orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK
    },
    isNotOrderableEligibility(result) {
      return result?.purchase?.orderStatus === PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY
    },
    formatPrice(value) {
      const normalized = Number(value)
      if (Number.isNaN(normalized)) return 'CHF 0.00'
      return `CHF ${normalized.toFixed(2)}`
    },
    getStatusText(result) {
      if (!result?.purchase) return null

      if (result.purchase.orderStatus === PRODUCT_ORDER_STATUS.LOCATION_REQUIRED) {
        return this.$T('orderLocationRequired')
      }
      if (result.purchase.orderStatus === PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY) {
        return this.$T('notOrderableEligibility')
      }
      if (result.purchase.orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK) {
        return this.$T('notifyMe')
      }

      return null
    },
    async onNotifyClick(result) {
      if (!this.isLoggedIn || !result?.id || !this.isOutOfStock(result)) {
        return
      }

      const toggleResult = await toggleProductRestockSubscription({
        productExternalId: result.id,
        toast: this.$bvToast,
        t: key => this.$T(key)
      })
      if (toggleResult.success && typeof toggleResult.subscribed === 'boolean') {
        this.$set(this.restockSubscribedByProductId, result.id, toggleResult.subscribed)
      }
    }
  }
}
</script>

<style lang="scss">
.search-result-products {
  /* Marketing basket can extend to card edge; default teaser overflow:hidden would clip the cart icon */
  .teaser.img-type-marketingproduct {
    overflow: visible;
  }

  .search-marketing-purchase {
    margin-top: 0;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .purchase-basket-select {
      overflow: visible;
    }

    /* ProductBasketbutton.small fixes .basketbutton-quantity to 2.5rem — marketing: use full row share like browser devtools */
    .purchase-basket-select .basketbutton.small .basketbutton-quantity {
      width: 100%;
    }

    .purchase-basket-select .basketbutton.small .basketbutton-quantity .dropdown {
      width: 100%;
    }

    .purchase-basket-select .basketbutton.small .basketbutton-quantity .dropdown-toggle {
      max-width: 100%;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      word-break: break-word;
      line-height: 1.2;
      text-align: center;
    }

    .purchase-basket-select .basketbutton-text {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding-left: $spacer * 0.35;
      padding-right: $spacer * 0.35;
    }

    .purchase-basket-select .basketbutton-text .bi {
      font-size: 1rem;
    }

    .purchase-basket-select .dropdown-toggle {
      min-height: 1.55rem;
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  .teaser-link {
    color: $body-color;
    text-decoration: none;
  }

  .card-footer {
    padding-right: 1rem;
    padding-left: 1rem;

    // ensure footer controls are above link
    z-index: 2;
  }

  .search-product-purchase {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: $spacer * 0.35;
    flex-wrap: nowrap;
    min-width: 0;
    width: 100%;

    .purchase-availability {
      margin-right: $spacer * 0.5;
    }

    .purchase-price {
      white-space: nowrap;

      .price-value {
        color: $gray-700;
      }
    }

    .availability-icon.available {
      color: $success;
    }

    .availability-icon.not-available {
      color: $black;
    }

    .purchase-cart {
      display: inline-flex;
      align-items: center;
      margin-left: $spacer * 0.15;
    }

    .purchase-basket-select {
      overflow: visible;
    }

    .purchase-basket-select .basketbutton-text {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding-left: $spacer * 0.35;
      padding-right: $spacer * 0.35;
    }

    .purchase-basket-select .basketbutton-text .bi {
      font-size: 1rem;
    }

    .purchase-basket-select .dropdown-toggle {
      min-height: 1.55rem;
      padding-top: 0;
      padding-bottom: 0;
    }

    .purchase-action-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.1rem;
      height: 2.1rem;
      padding: 0;
      border-radius: $border-radius;

      &.is-primary {
        background-color: $primary;
        border-color: $primary;
        color: color-yiq($primary);
      }

      &.is-muted {
        background-color: $gray-500;
        border-color: $gray-500;
        color: $white;
      }
    }
  }
}
</style>
